import { MM_FIT_EPS } from './fit-mm'

/**
 * Format ONE millimetre value for display.
 *
 * Issue #126 residual: the read-only "Size (mm)" columns used `.toFixed(0)`,
 * so a 7.5mm region read "8". Region sizes come out of split ratios, which are
 * NEVER snapped (Canvas.tsx drags write the raw ratio), so laid-out mm are
 * arbitrary floats — the display has to round, but 0 dp is a lie and 2+ dp is
 * noise given the 0.5mm-step inputs that author these values. 1 dp it is, the
 * same precision the ZPL pipeline already uses for mm in its own messages.
 *
 * Rounds to 1 decimal FIRST, then drops the decimal if the rounded value is
 * whole — that ordering is what makes it FP-residue-proof: split arithmetic
 * emits things like 30.000000000000004 (a subtraction residue), which must
 * read "30", not "30.0".
 *
 *   7.5                 → "7.5"
 *   30                  → "30"
 *   30.000000000000004  → "30"
 *   22.499999999999996  → "22.5"
 *   8.3333              → "8.3"
 *
 * Formats a single number on purpose: call sites keep their own separators
 * (the tables render `100 × 80` spaced, the zone badge renders `60×30 mm`
 * unspaced).
 */
export function formatMm(n: number): string {
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/**
 * Snap window for the DIRECTIONAL formatters, expressed in the tenths-of-a-mm
 * unit `n * 10` lives in — i.e. 1e-7 mm on the value itself.
 *
 * Two constraints pin it, and they pin it from both sides:
 *
 * - It must be WIDER than split residue, or `formatMmFloor` would print the
 *   44.99999999999999 mm zone (see fit-mm.ts) as "44.9". Residue off
 *   `parent.w * ratio` on label-sized numbers is ~1e-13 tenths, six orders of
 *   magnitude below this window.
 * - It must be NARROWER than half the fit tolerance — see the proof on
 *   `formatMmCeil` — which puts the ceiling at `5 * MM_FIT_EPS` tenths. Using
 *   MM_FIT_EPS itself lands five times inside that.
 *
 * So it is deliberately derived from MM_FIT_EPS rather than hand-picked: widen
 * the fit tolerance and this window widens with it, keeping the guarantee. A
 * flat 1e-4 here (a tempting "obviously tiny" constant) would break it — a
 * 2e-6 mm shortfall would snap BOTH sides to the same tenth and print the
 * self-contradicting message this whole exercise exists to kill.
 */
const TENTH_SNAP_EPS = MM_FIT_EPS

/** Round `n * 10` outward with `outward`, after absorbing FP residue. */
function toTenths(n: number, outward: (x: number) => number): number {
  const x = n * 10
  const nearest = Math.round(x)
  return Math.abs(x - nearest) < TENTH_SNAP_EPS ? nearest : outward(x)
}

/** Render an integer count of tenths the way `formatMm` renders its value. */
function renderTenths(tenths: number): string {
  const r = tenths / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/**
 * Format an AVAILABLE millimetre value — a capacity — for a refusal message.
 *
 * `formatMm` rounds to the nearest tenth, which is right for a display of what
 * something IS but wrong for a statement of what a user has to work with: a
 * 59.96 mm slot rounds to "60" and then refuses a 60 mm block, so the message
 * reads "needs 60×30 mm, slot is only 60×40 mm" and there is nothing to act on
 * (issue #126). Flooring understates the slot instead, so the quoted capacity
 * is always a width that genuinely fits.
 *
 *   59.96               → "59.9"    (never "60" — that width does NOT fit)
 *   44.99999999999999   → "45"      (split residue, not a real shortfall)
 *   30                  → "30"
 *   8.3999              → "8.3"
 */
export function formatMmFloor(n: number): string {
  return renderTenths(toTenths(n, Math.floor))
}

/**
 * Format a REQUIRED millimetre value — a requirement — for a refusal message.
 *
 * The mirror of `formatMmFloor`: ceiling overstates the requirement, so the
 * quoted need is always a width that genuinely does not fit.
 *
 * Together the pair delivers the property the refusal copy depends on:
 *
 *   required > available + MM_FIT_EPS  ⟹  formatMmCeil(required) reads
 *                                          strictly larger than
 *                                          formatMmFloor(available)
 *
 * Proof: write F, C for the tenths the two return. Snapping moves a value by
 * less than TENTH_SNAP_EPS = τ and rounding outward never moves it the wrong
 * way, so F ≤ 10·available + τ and C ≥ 10·required − τ. With
 * 10·required > 10·available + 10·MM_FIT_EPS this gives
 * C > F + 10·MM_FIT_EPS − 2τ, which is C > F for any τ < 5·MM_FIT_EPS. Both are
 * integers, so C ≥ F + 1: the printed numbers differ by at least one tenth, in
 * the direction that explains the refusal. Asserted over the whole reported
 * shortfall band in format-mm.test.ts.
 *
 *   55.00000000000001   → "55"      (split residue, not a real overflow)
 *   45.000002           → "45.1"
 *   60                  → "60"
 */
export function formatMmCeil(n: number): string {
  return renderTenths(toTenths(n, Math.ceil))
}
